import { useCallback, useEffect, useState } from "react";

export type TimeUnit = "days" | "hours";

export interface ModalVisibilityConfig {
  storageKey: string;
  lastClosedKey: string;
  temporaryHideDuration: number;
  temporaryHideUnit: TimeUnit;
}

export function useModalVisibilityControl(config: ModalVisibilityConfig) {
  const [shouldHide, setShouldHide] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(config.storageKey);
      const lastClosed = localStorage.getItem(config.lastClosedKey);

      // 永続的に非表示設定がある場合
      if (stored === "true") {
        setShouldHide(true);
        setIsLoaded(true);
        return;
      }

      // 最後に閉じた日時をチェック
      if (lastClosed) {
        const lastClosedTime = new Date(lastClosed);
        const now = new Date();

        let shouldHideBasedOnTime = false;

        if (config.temporaryHideUnit === "days") {
          // 日単位での比較（同じ日かどうか）
          const isSameDay =
            lastClosedTime.getFullYear() === now.getFullYear() &&
            lastClosedTime.getMonth() === now.getMonth() &&
            lastClosedTime.getDate() === now.getDate();

          if (config.temporaryHideDuration === 1) {
            // 1日の場合は同じ日かどうかで判定
            shouldHideBasedOnTime = isSameDay;
          } else {
            // 複数日の場合は日数で判定
            const diffDays =
              (now.getTime() - lastClosedTime.getTime()) /
              (1000 * 60 * 60 * 24);
            shouldHideBasedOnTime = diffDays < config.temporaryHideDuration;
          }
        } else if (config.temporaryHideUnit === "hours") {
          // 時間単位での比較
          const diffHours =
            (now.getTime() - lastClosedTime.getTime()) / (1000 * 60 * 60);
          shouldHideBasedOnTime = diffHours < config.temporaryHideDuration;
        }

        if (shouldHideBasedOnTime) {
          setShouldHide(true);
        } else {
          // 期間経過したので一時的な非表示設定をクリア
          localStorage.removeItem(config.lastClosedKey);
          setShouldHide(false);
        }
      } else {
        setShouldHide(false);
      }
    } catch (error) {
      console.warn("Failed to read from localStorage:", error);
      setShouldHide(false);
    } finally {
      setIsLoaded(true);
    }
  }, [config]);

  const setHideModal = useCallback(
    (hide: boolean) => {
      try {
        if (hide) {
          // 永続的に非表示
          localStorage.setItem(config.storageKey, "true");
          localStorage.removeItem(config.lastClosedKey);
        } else {
          localStorage.removeItem(config.storageKey);
          localStorage.removeItem(config.lastClosedKey);
        }
        setShouldHide(hide);
      } catch (error) {
        console.warn("Failed to write to localStorage:", error);
      }
    },
    [config],
  );

  const setTemporaryHide = useCallback(() => {
    try {
      // 一時的に非表示
      const now = new Date();
      localStorage.setItem(config.lastClosedKey, now.toISOString());
      setShouldHide(true);
    } catch (error) {
      console.warn("Failed to write to localStorage:", error);
    }
  }, [config]);

  const createShouldShowModal = useCallback(
    (customCondition?: () => boolean) => {
      return () => {
        if (!isLoaded) return false; // ローカルストレージ読み込み前は非表示
        if (shouldHide) return false; // 非表示設定がある場合は非表示

        // カスタム条件がある場合はそれを評価
        if (customCondition) {
          return customCondition();
        }

        // デフォルトでは表示
        return true;
      };
    },
    [shouldHide, isLoaded],
  );

  return {
    shouldHide,
    isLoaded,
    setHideModal,
    setTemporaryHide,
    createShouldShowModal,
  };
}
